// ===============================================================
// 🌐 VpnSettingsPage.js — VPN settings + privacy
// - Country change: setPremiumCountry (unchanged)
// - Toggles: updateVpnPrivacySettings (callable)
// path: src/settings/VpnSettingsPage.js
// ===============================================================

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { auth, db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";

import { getFunctions, httpsCallable } from "firebase/functions";

import "./VpnSettingsPage.css";
import { formatLocalizedDateTime } from "../utils/localizedDate";

const ALLOWED_COUNTRIES = [
  "sa", "ae", "kw", "qa", "bh", "om",
  "tr", "ir", "ru", "ua",
  "at", "de", "ch", "fr", "it", "es", "nl", "be", "se", "no", "dk", "uk",
  "sy", "lb", "jo", "eg", "tn", "dz", "ma",
];

// language is passed in explicitly by the caller (i18n.resolvedLanguage ||
// i18n.language) rather than importing the live i18n singleton here - this
// helper lives outside the component, so it stays side-effect-free and
// trivially testable. formatLocalizedDateTime's defaults (year, month
// short, day, hour 2-digit, minute 2-digit) already match this page's
// previous formatDateTimeEn options exactly, so no custom options object
// is passed.
function formatDate(ms, language) {
  if (!ms) return "";
  return formatLocalizedDateTime(ms, language);
}

function VpnSettingsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const activeLanguage = i18n.resolvedLanguage || i18n.language;

  const functions = getFunctions(undefined, "us-central1");
  const setPremiumCountryFn = useMemo(
    () => httpsCallable(functions, "setPremiumCountry"),
    [functions]
  );
  const updateVpnPrivacySettingsFn = useMemo(
    () => httpsCallable(functions, "updateVpnPrivacySettings"),
    [functions]
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [privacyBusy, setPrivacyBusy] = useState(false);

  const [premium, setPremium] = useState({
    active: false,
    expiresAtMs: 0,
    country: "TR",
  });

  const [gender, setGender] = useState("");
  const [msgSettings, setMsgSettings] = useState({
    vpnReadReceipts: false,
    vpnShowOnline: true,
    vpnVisibleCountry: false,
  });

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      navigate("/");
      return;
    }

    const ref = doc(db, "users", user.uid);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data() || {};
        const prem = data.premium || {};

        const expMs = prem.expiresAt?.toMillis ? prem.expiresAt.toMillis() : 0;

        const active = !!prem.active;

        const country = (prem.country || data.displayCountry || "TR")
          .toString()
          .trim()
          .toUpperCase();

        setPremium({
          active,
          expiresAtMs: expMs,
          country,
        });

        setGender(String(data.gender || "").trim().toLowerCase());

        const m = data.settings?.messages || {};
        setMsgSettings({
          vpnReadReceipts: m.vpnReadReceipts === true,
          vpnShowOnline: m.vpnShowOnline !== false,
          vpnVisibleCountry: m.vpnVisibleCountry === true,
        });

        setLoading(false);
      },
      (err) => {
        console.error("❌ premium snapshot error:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [navigate]);

  const premiumFullyActive = premium.active && premium.expiresAtMs > Date.now();
  const togglesDisabled = !premiumFullyActive || privacyBusy;
  const isFemale = gender === "female";

  const handlePick = async (codeLower) => {
    if (!premium.active) {
      alert(
        t("premium.loginRequired", {
          defaultValue: "يجب تفعيل الاشتراك أولاً لتغيير الدولة",
        })
      );
      return;
    }

    const code = codeLower.toUpperCase();

    if (code === premium.country) return;

    try {
      setSaving(true);
      await setPremiumCountryFn({ country: code });
      alert(
        t("premium.vpn.countryUpdated", {
          defaultValue: "✅ تم تغيير الدولة بنجاح",
        })
      );
    } catch (e) {
      console.error("setPremiumCountry error:", e);
      alert(
        t("premium.vpn.countryFailed", {
          defaultValue: "تعذر تغيير الدولة. تأكد من الاشتراك وحاول مرة أخرى",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  async function persistMessagesField(patch) {
    const prev = { ...msgSettings };
    setPrivacyBusy(true);
    setMsgSettings((s) => ({ ...s, ...patch }));
    try {
      await updateVpnPrivacySettingsFn(patch);
    } catch (e) {
      console.error("updateVpnPrivacySettings error:", e);
      setMsgSettings(prev);
      alert(
        t("premium.features.privacySaveFailed", {
          defaultValue: "تعذر حفظ إعدادات الخصوصية. حاول مرة أخرى.",
        })
      );
    } finally {
      setPrivacyBusy(false);
    }
  }

  if (loading) {
    return (
      <p className="vpn-loading">
        {t("common.loading", { defaultValue: "Loading..." })}
      </p>
    );
  }

  const expiresText = premium.expiresAtMs
    ? formatDate(premium.expiresAtMs, activeLanguage)
    : "";

  return (
    <div className="vpn-root">
      <header className="vpn-header">
        <button
          className="vpn-back"
          onClick={() => navigate(-1)}
          aria-label={t("back", { defaultValue: "رجوع" })}
          type="button"
        >
          ‹
        </button>

        <h3>{t("premium.vpn.title", { defaultValue: "إعدادات VPN" })}</h3>
        <span />
      </header>

      <section className="vpn-card">
        <p>
          {t("premium.vpn.status", { defaultValue: "حالة الاشتراك:" })}{" "}
          {premium.active
            ? "✔ " + t("premium.active", { defaultValue: "Active" })
            : "❌ " + t("premium.inactive", { defaultValue: "Inactive" })}
        </p>

        <p>
          {t("premium.vpn.currentCountry", { defaultValue: "الدولة الحالية:" })}{" "}
          <b>{premium.country}</b>
        </p>

        {expiresText ? (
          <p>
            {t("premium.expiresAt", { defaultValue: "ينتهي في:" })}{" "}
            <span className="vpn-ltr">{expiresText}</span>
          </p>
        ) : null}

        {!premium.active ? (
          <p style={{ opacity: 0.85, marginTop: 8 }}>
            {t("premium.vpn.subscribeToChangeCountry", {
              defaultValue: "لتغيير الدولة يجب تفعيل الاشتراك أولاً.",
            })}
          </p>
        ) : null}
      </section>

      <section className="vpn-card vpn-card--features">
        <h4 className="vpn-card-title">
          {t("premium.features.title", { defaultValue: "ميزات VPN" })}
        </h4>
        {isFemale ? (
          <ul className="vpn-features-list">
            <li>
              {t("premium.features.hideOnlineLastSeen", {
                defaultValue: "إخفاء متصل الآن / آخر ظهور",
              })}
            </li>
            <li>
              {t("premium.features.changeDisplayCountry", {
                defaultValue: "تغيير دولة الظهور",
              })}
            </li>
            <li>
              {t("premium.features.boostComingSoon", {
                defaultValue: "زيادة الظهور لاحقاً / Boost قريباً",
              })}
            </li>
          </ul>
        ) : (
          <ul className="vpn-features-list">
            <li>
              {t("premium.features.readReceiptsVisibility", {
                defaultValue: "رؤية قراءة الرسائل ✓✓",
              })}
            </li>
            <li>
              {t("premium.features.seeOnlineLastSeen", {
                defaultValue: "معرفة متصل الآن / آخر ظهور",
              })}
            </li>
            <li>
              {t("premium.features.changeDisplayCountry", {
                defaultValue: "تغيير دولة الظهور",
              })}
            </li>
          </ul>
        )}
      </section>

      <section className="vpn-card vpn-card--toggles">
        <h4 className="vpn-card-title">
          {t("premium.features.privacyTitle", { defaultValue: "خصوصية" })}
        </h4>
        {!premiumFullyActive ? (
          <p className="vpn-toggles-hint">
            {t("premium.features.activateToUse", {
              defaultValue: "فعّل الاشتراك لاستخدام هذه الميزات",
            })}
          </p>
        ) : null}

        {isFemale ? (
          <>
            <label className="vpn-toggle-row">
              <span className="vpn-toggle-label">
                {t("premium.features.toggleHideOnlineLastSeen", {
                  defaultValue: "إخفاء متصل الآن وآخر ظهور",
                })}
              </span>
              <input
                type="checkbox"
                className="vpn-toggle-input"
                checked={msgSettings.vpnShowOnline === false}
                disabled={togglesDisabled}
                onChange={(e) => {
                  if (togglesDisabled) return;
                  const hideOn = e.target.checked;
                  const vpnShowOnline = !hideOn;
                  void persistMessagesField({ vpnShowOnline });
                }}
              />
            </label>
            <label className="vpn-toggle-row">
              <span className="vpn-toggle-label">
                {t("premium.features.toggleUseVpnCountry", {
                  defaultValue: "استخدام دولة VPN",
                })}
              </span>
              <input
                type="checkbox"
                className="vpn-toggle-input"
                checked={msgSettings.vpnVisibleCountry}
                disabled={togglesDisabled}
                onChange={(e) => {
                  if (togglesDisabled) return;
                  const v = e.target.checked;
                  void persistMessagesField({ vpnVisibleCountry: v });
                }}
              />
            </label>
          </>
        ) : (
          <>
            <label className="vpn-toggle-row">
              <span className="vpn-toggle-label">
                {t("premium.features.toggleShowReadReceipts", {
                  defaultValue: "إظهار قراءة الرسائل ✓✓",
                })}
              </span>
              <input
                type="checkbox"
                className="vpn-toggle-input"
                checked={msgSettings.vpnReadReceipts}
                disabled={togglesDisabled}
                onChange={(e) => {
                  if (togglesDisabled) return;
                  const v = e.target.checked;
                  void persistMessagesField({ vpnReadReceipts: v });
                }}
              />
            </label>
            <label className="vpn-toggle-row">
              <span className="vpn-toggle-label">
                {t("premium.features.toggleUseVpnCountry", {
                  defaultValue: "استخدام دولة VPN",
                })}
              </span>
              <input
                type="checkbox"
                className="vpn-toggle-input"
                checked={msgSettings.vpnVisibleCountry}
                disabled={togglesDisabled}
                onChange={(e) => {
                  if (togglesDisabled) return;
                  const v = e.target.checked;
                  void persistMessagesField({ vpnVisibleCountry: v });
                }}
              />
            </label>
          </>
        )}
      </section>

      <section className="vpn-countries">
        <div className="vpn-countries-title">
          {t("premium.vpn.pickCountry", { defaultValue: "اختر دولة الظهور" })}
        </div>

        <div className="vpn-grid">
          {ALLOWED_COUNTRIES.map((c) => {
            const code = c.toUpperCase();
            const isActive = code === premium.country;

            return (
              <button
                key={c}
                type="button"
                className={`vpn-country ${isActive ? "active" : ""}`}
                onClick={() => handlePick(c)}
                disabled={saving || !premium.active}
                title={t("countries.code_title", {
                  defaultValue: "Country code: {{code}}",
                  code,
                })}
              >
                <span className="vpn-country-name">
                  {t(`countries.${c}`, { defaultValue: code })}
                </span>
                <span className="vpn-country-code">{code}</span>
              </button>
            );
          })}
        </div>

        {saving ? (
          <div className="vpn-saving">
            {t("premium.processing", { defaultValue: "جارٍ المعالجة..." })}
          </div>
        ) : null}
      </section>
    </div>
  );
}

export default VpnSettingsPage;
